import { sql } from 'drizzle-orm'
import {
  bigint,
  customType,
  date,
  decimal,
  index,
  PgColumn,
  pgSequence,
  timestamp,
} from 'drizzle-orm/pg-core'

// Define JSON types for jsonb columns
export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json }
  | Json[]
export type JsonRootData = { [key: string]: Json }

// Custom jsonb type that properly types the data
export const typedJsonb = <T = JsonRootData>(name: string) =>
  customType<{ data: T; driverData: string }>({
    dataType() {
      return 'jsonb'
    },
    toDriver(value: T): string {
      return JSON.stringify(value)
    },
    fromDriver(value: string | T): T {
      // Handle case where postgres already returns parsed JSON
      if (typeof value === 'object' && value !== null) {
        return value as T
      }
      // Handle case where value is a JSON string
      if (typeof value === 'string') {
        return JSON.parse(value) as T
      }
      // Return null/undefined as-is
      return value as T
    },
  })(name)

// Exported for Drizzle to use
export const globalIdSequenceForDrizzle = pgSequence('global_id_seq', {
  cache: 10,
})

export const getTimestampWithTimezone = () => {
  return timestamp({
    withTimezone: true,
    mode: 'string',
  })
}

// A calendar date with no time-of-day or timezone. Use for fields that mean a
// day (e.g. an invoice due date / eräpäivä), not an instant. Returned as a
// 'YYYY-MM-DD' string, so dayjs()/new Date() parse it directly.
export const getDateField = () => {
  return date({ mode: 'string' })
}

export const getId = () => {
  return bigint('id', { mode: 'number' })
    .primaryKey()
    .default(sql`nextval('global_id_seq')`)
}

// Required foreign key reference
export const getIdReference = (column: () => PgColumn) => {
  return bigint({ mode: 'number' }).notNull().references(column)
}

// Optional/nullable foreign key reference
export const getNullableIdReference = (column: () => PgColumn) => {
  return bigint({ mode: 'number' }).references(column)
}

export const getBaseFields = () => ({
  id: getId(),
  createdAt: getTimestampWithTimezone().notNull().defaultNow(),
  updatedAt: getTimestampWithTimezone().notNull().defaultNow(),
  deletedAt: getTimestampWithTimezone(),
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getBaseIndexes = (tableName: string, table: any) => {
  return [
    index(`${tableName}_created_at_idx`).on(table.createdAt),
    index(`${tableName}_updated_at_idx`).on(table.updatedAt),
    index(`${tableName}_deleted_at_idx`).on(table.deletedAt),
  ]
}

// Monetary field helper - stores values in euros with 2 decimal places
export const getMonetaryField = () => {
  return decimal({ precision: 10, scale: 2 })
}

// Precise monetary field - stores values with 5 decimal places
// Used for unit prices where VAT reverse-calculation requires extra precision
export const getPreciseMonetaryField = () => {
  return decimal({ precision: 12, scale: 5 })
}
